import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  OfficialVaultRequestInput,
  getFaucetBankInfo,
  listOfficialVaultRequests,
  approveVaultApplication,
  redriveOfficialVaultRequest,
  rejectVaultApplication,
  requestOfficialVault,
  sendTestnetFaucetTransfer,
} from 'src/pages/admin/services/officialVaultService';
import { listVaults } from 'src/pages/admin/services/vaultService';
import { parseApiError } from 'src/pages/admin/utils/decimal-input';

import { fieldUnits } from '../params/helpers';
import { useVaultParams } from '../params/useVaultParams';
import { OFFICIAL_VAULT_ERROR, OFFICIAL_VAULTS_NS as NS } from './constants';
import {
  ApplicationView,
  CatalogRowState,
  EMPTY_DRAFT,
  EMPTY_FAUCET_DRAFT,
  FaucetAttempt,
  FaucetBalance,
  FaucetBank,
  FaucetDraft,
  FaucetFieldErrors,
  freeVaultId,
  FieldErrors,
  RequestBounds,
  RequestDraft,
  RequestDraftKey,
  SummaryRow,
  boundsFrom,
  catalogRowsOf,
  conflictField,
  faucetAttemptFor,
  faucetBank,
  faucetErrorCode,
  faucetErrorMessages,
  isApplicationMoving,
  isMaybeRecorded,
  listErrorMessage,
  newIdempotencyKey,
  redriveErrorMessages,
  reviewErrorDetail,
  reviewErrorMessages,
  validateRejectReason,
  requestSummary,
  sortByRequestedAtDesc,
  submitErrorMessages,
  suggestVaultId,
  toFaucetInput,
  toRequestInput,
  faucetBalance,
  validateFaucetDraft,
  validateRequestDraft,
  withVaultStates,
} from './helpers';

/**
 * The catalog's vault states by id; null when the vault list cannot be read (no `vault:vaults read`, or the list is
 * down). The page then keeps the states it already has, and on a first read falls back to the C-08 status alone.
 */
const vaultStates = async (): Promise<Map<string, CatalogRowState> | null> => {
  try {
    return new Map(
      (await listVaults()).map((v) => [
        v.vaultId,
        { state: v.state, raised: Boolean(v.raisingStartedAt) },
      ])
    );
  } catch {
    return null;
  }
};

/** How often the list re-reads itself while a row is still moving (XBIT-4383; f1's pick, not a PO number). */
export const POLL_INTERVAL_MS = 10_000;

/**
 * `GET /vault/official-vaults`, newest first, each with its catalog row's state. While any row is moving
 * (isApplicationMoving) and the tab is visible, it re-reads itself every POLL_INTERVAL_MS, silently: no loading flag,
 * and a failed poll keeps the list it has. A read lands only if no newer read has landed first, so a slow poll never
 * undoes a refetch, and a newer poll that fails never drops one.
 */
export const useOfficialVaultRequests = () => {
  const translate = useTranslate();
  // react-admin's translate is not identity-stable; a ref keeps refetch stable (see useVaultParams).
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const [requests, setRequests] = useState<ApplicationView[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const requestsRef = useRef(requests);
  requestsRef.current = requests;
  // Reads are numbered as they start; `landed` is the newest one applied.
  const started = useRef(0);
  const landed = useRef(0);

  const load = useCallback(async (silent: boolean) => {
    started.current += 1;
    const read = started.current;
    if (!silent) setLoading(true);
    try {
      const [list, states] = await Promise.all([listOfficialVaultRequests(), vaultStates()]);
      if (read < landed.current) return;
      landed.current = read;
      const rows = states ?? catalogRowsOf(requestsRef.current);
      setRequests(withVaultStates(sortByRequestedAtDesc(list), rows));
      setLoadError(null);
    } catch (error) {
      if (!silent && read > landed.current)
        setLoadError(listErrorMessage(parseApiError(error), translateRef.current));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const refetch = useCallback(() => load(false), [load]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const moving = requests.some((r) => isApplicationMoving(r, Date.now()));
  useEffect(() => {
    if (!moving) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [moving, load]);

  return { requests, loading, loadError, refetch };
};

/**
 * The bounds a request is checked against: the same GET /vault/params the Parameters page reads.
 * Requesting therefore also needs `vault:params read`; a 403 there is reported as that missing
 * permission (`forbidden`), not as a failure a refresh could fix.
 */
export const useRequestBounds = () => {
  const translate = useTranslate();
  const { params, loading, loadError, loadErrorStatus, refetch } = useVaultParams();
  const bounds = useMemo(() => boundsFrom(params), [params]);
  const forbidden = Boolean(loadError) && loadErrorStatus === 403;
  let boundsError: string | null = null;
  if (loadError) boundsError = translate(`${NS}.${forbidden ? 'boundsForbidden' : 'boundsFailed'}`);
  return { bounds, loading, loadError: boundsError, forbidden, refetch };
};

export interface FormAlert {
  severity: 'error' | 'warning';
  messages: string[];
}

interface FormOptions {
  bounds: RequestBounds | null;
  /** Re-reads the list; called after every submit, success or not. */
  onSettled: () => Promise<unknown> | void;
  refetchBounds: () => Promise<unknown> | void;
  /** Vault IDs a typed name must not generate: the FAILED ones, which C-08 keeps for good (XBIT-4321). */
  takenVaultIds?: ReadonlySet<string>;
}

const NO_IDS: ReadonlySet<string> = new Set();

/**
 * edit → confirm → submit. A failed submit returns to edit with every value kept, so
 * "submit again" after VAULT_ONBOARDING_NOT_PUBLISHED sends the identical body (design §5.1).
 */
export const useOfficialVaultForm = ({
  bounds,
  onSettled,
  refetchBounds,
  takenVaultIds = NO_IDS,
}: FormOptions) => {
  const translate = useTranslate();
  const notify = useNotify();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'edit' | 'confirm'>('edit');
  const [draft, setDraft] = useState<RequestDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [alert, setAlert] = useState<FormAlert | null>(null);
  const [summary, setSummary] = useState<SummaryRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // The form was opened by Retry on a FAILED request (XBIT-4321).
  const [retrying, setRetrying] = useState(false);
  // A second click before the first POST settles must not send twice.
  const inFlight = useRef(false);
  // The body the confirm step showed: submit sends exactly this, never a re-read of the draft.
  const reviewed = useRef<OfficialVaultRequestInput | null>(null);

  const taken = useRef(takenVaultIds);
  taken.current = takenVaultIds;

  /** Empty, or prefilled (Retry, XBIT-4321) with terms the operator reviews before anything is sent. */
  const openDialog = useCallback(
    (prefill?: RequestDraft) => {
      setDraft(prefill ?? EMPTY_DRAFT);
      setRetrying(Boolean(prefill));
      setErrors({});
      setAlert(null);
      setSummary([]);
      reviewed.current = null;
      setStep('edit');
      setOpen(true);
      refetchBounds(); // check against the parameters as they are now, not at page load
    },
    [refetchBounds]
  );

  const closeDialog = useCallback(() => {
    if (!inFlight.current) setOpen(false);
  }, []);

  // The vault ID is read-only: the name writes it, the operator never does (PO, 2026-10-01).
  const setField = useCallback((key: RequestDraftKey, value: string) => {
    if (key === 'vaultId') return;
    setDraft((current) => {
      const next = { ...current, [key]: value };
      if (key === 'name') next.vaultId = freeVaultId(suggestVaultId(value), taken.current);
      return next;
    });
  }, []);

  const review = useCallback(() => {
    if (!bounds) {
      setAlert({ severity: 'error', messages: [translate(`${NS}.boundsUnavailable`)] });
      return;
    }
    const nextErrors = validateRequestDraft(draft, bounds);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setAlert({ severity: 'error', messages: [translate(`${NS}.errors.fixFields`)] });
      return;
    }
    setAlert(null);
    const input = toRequestInput(draft);
    reviewed.current = input;
    setSummary(requestSummary(input, bounds, fieldUnits(translate)));
    setStep('confirm');
  }, [bounds, draft, translate]);

  const back = useCallback(() => setStep('edit'), []);

  const submit = useCallback(async () => {
    const input = reviewed.current;
    if (inFlight.current || !input) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      await requestOfficialVault(input);
      setOpen(false);
      notify(translate(`${NS}.requested`), { type: 'success' });
    } catch (error) {
      const info = parseApiError(error);
      // A rule failed server-side: the parameters may have moved since the form opened.
      if (info.code === OFFICIAL_VAULT_ERROR.INVALID || info.status === 422) refetchBounds();
      // A taken vault name is the Name field's error; other conflicts keep the banner alone.
      if (conflictField(error) === 'name') setErrors({ name: `${NS}.errors.nameTaken` });
      setAlert({
        severity: isMaybeRecorded(info) ? 'warning' : 'error',
        messages: submitErrorMessages(info, translate),
      });
      setStep('edit');
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      await onSettled();
    }
  }, [notify, onSettled, refetchBounds, translate]);

  return {
    open,
    step,
    retrying,
    draft,
    errors,
    alert,
    summary,
    submitting,
    openDialog,
    closeDialog,
    setField,
    review,
    back,
    submit,
  };
};

export type OfficialVaultForm = ReturnType<typeof useOfficialVaultForm>;

/**
 * Approve (STORY-22 AC3, XBIT-4257; Option B, XBIT-4366): one POST with the vault id alone — the vault opens ACTIVE, no
 * raise window. A refusal keeps the dialog and the error and offers Retry (the application stays Pending); approving
 * again replays a recorded approval.
 */
export const useApproveForm = ({ onSettled }: { onSettled: () => Promise<unknown> | void }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [vaultId, setVaultId] = useState<string | null>(null);
  const [alert, setAlert] = useState<FormAlert | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  const openDialog = useCallback((id: string) => {
    setAlert(null);
    setVaultId(id);
  }, []);

  const closeDialog = useCallback(() => {
    if (!inFlight.current) setVaultId(null);
  }, []);

  const submit = useCallback(async () => {
    if (inFlight.current || !vaultId) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      await approveVaultApplication(vaultId);
      setVaultId(null);
      notify(translate(`${NS}.review.approved`), { type: 'success' });
    } catch (error) {
      // No retry_after on approve: a repeat is the same decision and the engine retries the open at once.
      const detail = reviewErrorDetail(error);
      setAlert({
        severity: 'error',
        messages: reviewErrorMessages(parseApiError(error), detail, translate),
      });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      await onSettled();
    }
  }, [notify, onSettled, translate, vaultId]);

  return { vaultId, alert, submitting, openDialog, closeDialog, submit };
};

export type ApproveForm = ReturnType<typeof useApproveForm>;

/**
 * Resume from the failed step (XBIT-4240, C-08 v3.1): one POST with the vault id alone. A refusal keeps the dialog and its
 * reason (nothing was written); a success closes it and says whether the wake-up went out. Re-driving again after a
 * success is refused as "not failed" by the engine, never repeated.
 */
export const useRedriveForm = ({ onSettled }: { onSettled: () => Promise<unknown> | void }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [vaultId, setVaultId] = useState<string | null>(null);
  const [alert, setAlert] = useState<FormAlert | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  const openDialog = useCallback((id: string) => {
    setAlert(null);
    setVaultId(id);
  }, []);

  const closeDialog = useCallback(() => {
    if (!inFlight.current) setVaultId(null);
  }, []);

  const submit = useCallback(async () => {
    if (inFlight.current || !vaultId) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      const out = await redriveOfficialVaultRequest(vaultId);
      setVaultId(null);
      notify(
        translate(out.wakeUpSent ? `${NS}.redrive.done` : `${NS}.redrive.doneNoWakeUp`, {
          step: out.fromStep,
        }),
        { type: out.wakeUpSent ? 'success' : 'warning' }
      );
    } catch (error) {
      setAlert({
        severity: 'error',
        messages: redriveErrorMessages(parseApiError(error), translate),
      });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      await onSettled();
    }
  }, [notify, onSettled, translate, vaultId]);

  return { vaultId, alert, submitting, openDialog, closeDialog, submit };
};

export type RedriveForm = ReturnType<typeof useRedriveForm>;

/** Reject (STORY-22 AC4, XBIT-4257): a reason (1–500 characters, shown to the applicant), then one POST. */
export const useRejectForm = ({ onSettled }: { onSettled: () => Promise<unknown> | void }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [vaultId, setVaultId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [alert, setAlert] = useState<FormAlert | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [retryAfter, setRetryAfter] = useState<number | undefined>();
  const inFlight = useRef(false);

  const openDialog = useCallback((id: string, prefill = '') => {
    setReason(prefill);
    setError(undefined);
    setAlert(null);
    setRetryAfter(undefined);
    setVaultId(id);
  }, []);

  const closeDialog = useCallback(() => {
    if (!inFlight.current) setVaultId(null);
  }, []);

  const submit = useCallback(async () => {
    if (inFlight.current || !vaultId) return;
    const invalid = validateRejectReason(reason);
    setError(invalid);
    if (invalid) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      await rejectVaultApplication(vaultId, reason.trim());
      setVaultId(null);
      notify(translate(`${NS}.review.rejected`), { type: 'success' });
    } catch (err) {
      const detail = reviewErrorDetail(err);
      setRetryAfter(detail.retryAfter);
      setAlert({
        severity: 'error',
        messages: reviewErrorMessages(parseApiError(err), detail, translate),
      });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      await onSettled();
    }
  }, [notify, onSettled, reason, translate, vaultId]);

  return {
    vaultId,
    reason,
    error,
    alert,
    submitting,
    retryAfter,
    openDialog,
    closeDialog,
    setReason,
    submit,
  };
};

export type RejectForm = ReturnType<typeof useRejectForm>;

/**
 * `GET /vault/faucet/bank`, once. A failure is not reported to the operator: the faucet is an `unstable`-only
 * action and every stage without it answers with no network, which is indistinguishable from not offered.
 */
export const useFaucetBank = () => {
  const [bank, setBank] = useState<FaucetBank>({ offered: false });
  useEffect(() => {
    let live = true;
    getFaucetBankInfo()
      .then((info) => {
        if (live) setBank(faucetBank(info));
      })
      .catch(() => {
        if (live) setBank({ offered: false });
      });
    return () => {
      live = false;
    };
  }, []);
  return bank;
};

/**
 * Faucet dialog: the bank's balance is read by the engine from the venue when the dialog opens and again after a
 * send, so the operator sees what is left. A hidden bank has no address, so the read is a no-op until it is offered.
 */
export const useFaucetForm = ({
  bank,
  onSettled,
}: {
  bank: FaucetBank;
  onSettled: () => Promise<unknown> | void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [vaultId, setVaultId] = useState<string | null>(null);
  const [draft, setDraft] = useState<FaucetDraft>(EMPTY_FAUCET_DRAFT);
  // XBIT-4400: the suggested amount was capped at the faucet ceiling; the dialog says the transfer lands short.
  const [capped, setCapped] = useState<{ max: string; needed: string } | null>(null);
  const [errors, setErrors] = useState<FaucetFieldErrors>({});
  const [alert, setAlert] = useState<FormAlert | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [balance, setBalance] = useState<FaucetBalance>({ known: false });
  const [balanceLoading, setBalanceLoading] = useState(false);
  const inFlight = useRef(false);
  const attempt = useRef<FaucetAttempt | null>(null);
  const address = bank.offered ? bank.address : null;

  const readBalance = useCallback(async () => {
    if (!address) return;
    setBalanceLoading(true);
    try {
      // The engine reads the PERP withdrawable (what a usdSend can move) from the venue; null is unknown, not zero.
      setBalance(faucetBalance(await getFaucetBankInfo()));
    } catch {
      setBalance({ known: false });
    } finally {
      setBalanceLoading(false);
    }
  }, [address]);

  const openDialog = useCallback(
    (
      id: string,
      suggestion?: { amount: string; capped: boolean; needed: string } | null,
      // XBIT-4469 (D2): the manager's holder id while the stake is owed; '' or absent leaves it for the operator.
      userId?: string
    ) => {
      setDraft({ ...EMPTY_FAUCET_DRAFT, amount: suggestion?.amount ?? '', userId: userId ?? '' });
      setCapped(suggestion?.capped ? { max: suggestion.amount, needed: suggestion.needed } : null);
      setErrors({});
      setAlert(null);
      attempt.current = null;
      setVaultId(id);
      readBalance();
    },
    [readBalance]
  );

  const closeDialog = useCallback(() => {
    if (!inFlight.current) setVaultId(null);
  }, []);

  const setField = useCallback((key: keyof FaucetDraft, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }));
  }, []);

  const submit = useCallback(async () => {
    if (inFlight.current || !vaultId) return;
    const nextErrors = validateFaucetDraft(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    const next = faucetAttemptFor(
      attempt.current,
      toFaucetInput(vaultId, draft),
      newIdempotencyKey
    );
    attempt.current = next;
    inFlight.current = true;
    setSubmitting(true);
    try {
      const result = await sendTestnetFaucetTransfer({
        ...next.transfer,
        idempotencyKey: next.key,
      });
      attempt.current = null;
      setVaultId(null);
      notify(translate(`${NS}.faucet.${result.applied ? 'submitted' : 'alreadyApplied'}`), {
        type: 'success',
      });
      readBalance();
    } catch (error) {
      setAlert({
        severity: 'error',
        messages: faucetErrorMessages(parseApiError(error), faucetErrorCode(error), translate),
      });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      await onSettled();
    }
  }, [draft, notify, onSettled, readBalance, translate, vaultId]);

  return {
    vaultId,
    draft,
    capped,
    errors,
    alert,
    submitting,
    balance,
    balanceLoading,
    openDialog,
    closeDialog,
    setField,
    submit,
  };
};

export type FaucetForm = ReturnType<typeof useFaucetForm>;
